import React, { useState, useEffect } from 'react';
import { messagesApi } from '../api';
import { Card, PageTitle, Table, Badge, Btn, Input, Select, Pagination, StatCard } from '../components/common/UI';
import { RefreshCw, Trash2, MessageSquare, CheckCircle, XCircle, Clock } from 'lucide-react';
import toast from 'react-hot-toast';

const statusColor = { sent: 'blue', delivered: 'green', failed: 'red', pending: 'yellow', read: 'green' };

export default function Messages() {
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ page: 1, limit: 50, status: '', type: '', number: '' });
  const [clearDays, setClearDays] = useState(30);

  const load = async (f) => {
    const usedFilters = f || filters;
    setLoading(true);
    try {
      const params = {};
      Object.keys(usedFilters).forEach((k) => { if (usedFilters[k] !== '') params[k] = usedFilters[k]; });
      const [l, s, q] = await Promise.all([
        messagesApi.logs(params),
        messagesApi.stats({ days: 7 }),
        messagesApi.queueStatus(),
      ]);
      setLogs(l.data.data.logs);
      setPagination(l.data.data.pagination);
      setStats(s.data.data);
      setQueue(q.data.data);
    } catch (_e) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleFilter = (key, val) => {
    const f = Object.assign({}, filters, { [key]: val, page: key === 'page' ? val : 1 });
    setFilters(f);
    load(f);
  };

  const clearLogs = async () => {
    if (!window.confirm('Delete logs older than ' + clearDays + ' days?')) return;
    try {
      const { data } = await messagesApi.clearLogs({ olderThan: parseInt(clearDays, 10) });
      toast.success('Deleted ' + data.data.deletedCount + ' logs');
      load();
    } catch (_e) {
      toast.error('Clear failed');
    }
  };

  const overall = stats ? stats.overall : {};

  return (
    <div>
      <PageTitle sub="View and manage message history">Messages</PageTitle>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Total" value={overall.total ? overall.total.toLocaleString() : '0'} icon={MessageSquare} />
        <StatCard label="Delivered" value={overall.delivered ? overall.delivered.toLocaleString() : '0'} icon={CheckCircle} color="#25d366" />
        <StatCard label="Failed" value={overall.failed || 0} icon={XCircle} color="#ef4444" />
        <StatCard label="Queue Waiting" value={queue ? queue.waiting : 0} icon={Clock} color="#f59e0b" />
        <StatCard label="Queue Active" value={queue ? queue.active : 0} icon={RefreshCw} color="#3b82f6" />
      </div>

      <Card>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
          <div style={{ flex: '0 0 140px' }}>
            <Select label="Status" value={filters.status} onChange={(e) => handleFilter('status', e.target.value)}>
              <option value="">All</option>
              {['pending', 'sent', 'delivered', 'read', 'failed'].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
          </div>
          <div style={{ flex: '0 0 120px' }}>
            <Select label="Type" value={filters.type} onChange={(e) => handleFilter('type', e.target.value)}>
              <option value="">All</option>
              <option value="single">Single</option>
              <option value="bulk">Bulk</option>
            </Select>
          </div>
          <div style={{ flex: '1 1 180px' }}>
            <Input
              label="Phone"
              placeholder="Filter by number"
              value={filters.number}
              onChange={(e) => handleFilter('number', e.target.value)}
            />
          </div>
          <Btn size="sm" variant="secondary" onClick={() => load()}><RefreshCw size={14} /> Refresh</Btn>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginLeft: 'auto' }}>
            <input
              type="number"
              value={clearDays}
              onChange={(e) => setClearDays(e.target.value)}
              style={{ width: 60, padding: '7px 8px', background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 12, outline: 'none' }}
            />
            <span style={{ color: '#64748b', fontSize: 12 }}>days</span>
            <Btn size="sm" variant="danger" onClick={clearLogs}><Trash2 size={13} /> Clear</Btn>
          </div>
        </div>

        <Table
          loading={loading}
          headers={['Message ID', 'To', 'Message', 'Status', 'Type', 'Attempts', 'Sent At']}
          rows={logs.map((l) => [
            <code style={{ fontSize: 11, color: '#64748b' }}>{l.messageId ? l.messageId.slice(-12) : '—'}</code>,
            <span style={{ fontSize: 12 }}>+{l.to}</span>,
            <span style={{ fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{l.body}</span>,
            <Badge color={statusColor[l.status] || 'default'}>{l.status}</Badge>,
            <Badge>{l.type}</Badge>,
            l.attempts,
            l.sentAt ? new Date(l.sentAt).toLocaleString() : '—',
          ])}
        />
        <Pagination page={pagination.page} pages={pagination.pages} onPage={(p) => handleFilter('page', p)} />
        <p style={{ color: '#64748b', fontSize: 12, marginTop: 8 }}>
          Total: {pagination.total ? pagination.total.toLocaleString() : 0} records
        </p>
      </Card>
    </div>
  );
}
